/**
 * Parent side of the widget bridge.
 *
 * Deliberately NOT built on Utils/MessageBridge.js: that one posts with
 * targetOrigin "*" and has a `// In production, check event.origin here`
 * comment where the check should be. This bridge talks to untrusted,
 * model-authored code, so it validates every message.
 *
 * Origin model — the important part:
 *   The frame runs with sandbox="allow-scripts" and WITHOUT allow-same-origin,
 *   so it has an OPAQUE origin. Its messages therefore arrive with
 *   `event.origin === "null"` (the literal string). That means an origin
 *   allowlist alone is worthless — every opaque frame on the page looks
 *   identical. The real check is `event.source === iframe.contentWindow`,
 *   which is an object identity comparison the frame cannot forge.
 *
 *   For the same reason, parent→frame posts must use targetOrigin "*": an
 *   opaque origin cannot be named. This is safe ONLY because nothing secret
 *   is ever sent — see the invariant below.
 *
 * INVARIANT: no auth token, cookie, or session state crosses into a frame.
 * Data goes in by reference (tableId) and comes back as rows the server
 * already agreed to serve for this conversation.
 */

// Message types the parent will act on. Anything else is dropped and counted.
export const INBOUND = {
  RESIZE: "resize",
  SEND_PROMPT: "sendPrompt",
  OPEN_LINK: "openLink",
  REQUEST_DATA: "requestData"
};

export const OUTBOUND = {
  DATA: "data",
  ERROR: "error"
};

// Guards against a runaway widget pinning the main thread or the layout.
const MAX_HEIGHT = 2000;
const MIN_HEIGHT = 40;
const MAX_PROMPT = 500;

const SAFE_LINK = /^https?:\/\//i;

/**
 * Validate and normalize an inbound message. Returns null when the message
 * should be ignored. Pure, so it can be tested without a DOM.
 */
export const parseMessage = raw => {
  if (!raw || typeof raw !== "object") return null;
  const type = raw.type;

  switch (type) {
    case INBOUND.RESIZE: {
      const height = Number(raw.height);
      if (!Number.isFinite(height)) return null;
      // Clamp rather than reject: a widget mid-layout can report a silly
      // number, and dropping it would leave the frame stuck at its old size.
      return { type, height: Math.min(MAX_HEIGHT, Math.max(MIN_HEIGHT, Math.round(height))) };
    }
    case INBOUND.SEND_PROMPT: {
      const text = typeof raw.text === "string" ? raw.text.trim() : "";
      if (!text) return null;
      return { type, text: text.slice(0, MAX_PROMPT) };
    }
    case INBOUND.OPEN_LINK: {
      const url = typeof raw.url === "string" ? raw.url.trim() : "";
      // Scheme allowlist: javascript:, data: and blob: are all navigable and
      // all dangerous. Only http(s) leaves this building.
      if (!url || !SAFE_LINK.test(url)) return null;
      return { type, url };
    }
    case INBOUND.REQUEST_DATA: {
      const tableId = typeof raw.tableId === "string" ? raw.tableId.trim() : "";
      if (!tableId) return null;
      return { type, tableId, page: Number(raw.page) > 0 ? Math.floor(Number(raw.page)) : 1 };
    }
    default:
      return null;
  }
};

/**
 * Attach a listener for one frame. `handlers` receives already-validated
 * messages. Returns a detach function.
 */
export const attachWidgetBridge = ({ getFrame, handlers = {} }) => {
  const onMessage = event => {
    const frame = typeof getFrame === "function" ? getFrame() : null;
    // Identity check, not an origin string check — see the note above.
    if (!frame || !frame.contentWindow || event.source !== frame.contentWindow) return;

    const message = parseMessage(event.data);
    if (!message) return;

    const handler = handlers[message.type];
    if (handler) handler(message);
  };

  window.addEventListener("message", onMessage);
  return () => window.removeEventListener("message", onMessage);
};

/** Post to a sandboxed frame. targetOrigin must be "*" for an opaque origin. */
export const postToWidget = (frame, message) => {
  if (!frame || !frame.contentWindow) return;
  frame.contentWindow.postMessage(message, "*");
};
